import React, { useState } from 'react';

import { ArrowLeft, Paperclip } from 'lucide-react';
import { queryCache, useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { DotBricksLoading, Toast } from 'src/common/components';

import {
  assignRequest,
  commentOnRequest,
  fetchAssignableUsers,
  fetchRequest,
  requiresResolutionNote,
  updateRequestStatus,
  uploadPurchaseOrder,
} from '../api';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  COMMENT_MAX_LENGTH,
  INSTALLATION_TYPE_LABEL,
  REQUESTER_TEAM_LABEL,
  REQUESTS_QUERY_KEY,
  REQUEST_QUERY_KEY,
  REQUEST_TYPE_LABEL,
  SIP_TRUNK_REASON_LABEL,
  STATUS_LABEL,
  STATUS_OPTIONS,
} from '../constants';
import { AssignableUser, RequestStatus } from '../types';
import { callVolumeSummary, describeEvent, formatDate, formatDateTime } from '../utils';

import StatusBadge from './StatusBadge';

const ArrowLeft16 = (): JSX.Element => <ArrowLeft size={16} />;
const Paperclip14 = (): JSX.Element => <Paperclip size={14} />;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="py-1.5">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="text-sm">{children}</dd>
  </div>
);

const yesNo = (value: boolean): string => (value ? 'Yes' : 'No');

const RequestDetail: React.FC = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [nextStatus, setNextStatus] = useState<RequestStatus | ''>('');
  const [statusMessage, setStatusMessage] = useState('');
  const [comment, setComment] = useState('');

  const { data, isLoading } = useQuery([REQUEST_QUERY_KEY, requestId as string], fetchRequest, {
    enabled: !!requestId,
  });

  const request = data?.data;

  const invalidate = (): void => {
    queryCache.invalidateQueries(REQUEST_QUERY_KEY);
    queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
  };

  const [changeStatus, { isLoading: isChangingStatus }] = useMutation(
    () => updateRequestStatus(requestId as string, nextStatus as RequestStatus, statusMessage),
    {
      onSuccess: () => {
        invalidate();
        setNextStatus('');
        setStatusMessage('');
        Toast.success('Status updated.');
      },
      onError: () => {
        Toast.error('There was an error updating the status.');
      },
    }
  );

  const [addComment, { isLoading: isCommenting }] = useMutation(
    () => commentOnRequest(requestId as string, comment),
    {
      onSuccess: () => {
        invalidate();
        setComment('');
      },
      onError: () => {
        Toast.error('There was an error adding the note.');
      },
    }
  );

  const { data: assignableData } = useQuery(ASSIGNABLE_USERS_QUERY_KEY, fetchAssignableUsers);
  const assignableUsers: AssignableUser[] = assignableData?.data ?? [];

  // Holds the choice from the moment it is made until the refetch confirms it.
  // Without it the select re-reads `request.assignee` on the very next render
  // and visibly snaps back to the old name mid-flight. `undefined` means "no
  // change pending"; `null` is a real pending value (unassign).
  const [pendingAssignee, setPendingAssignee] = useState<number | null | undefined>(undefined);

  const [assign, { isLoading: isAssigning }] = useMutation(
    (assignee: number | null) => assignRequest(requestId as string, assignee),
    {
      onSuccess: () => {
        invalidate();
        setPendingAssignee(undefined);
        Toast.success('Assignee updated.');
      },
      onError: () => {
        // Drop back to whatever the server still believes.
        setPendingAssignee(undefined);
        Toast.error('There was an error assigning the request.');
      },
    }
  );

  const [attachPurchaseOrder, { isLoading: isUploading }] = useMutation(
    (file: File) => uploadPurchaseOrder(requestId as string, file),
    {
      onSuccess: () => {
        invalidate();
        Toast.success('Purchase order attached.');
      },
      onError: () => {
        Toast.error('There was an error uploading the purchase order.');
      },
    }
  );

  if (isLoading) return <DotBricksLoading />;
  if (!request) {
    return <p className="p-8 text-gray-500">This request no longer exists.</p>;
  }

  const sip = request.sip_trunk_detail;
  const equipment = request.queue_equipment_detail;
  const phoneHardware = request.phone_hardware_detail;

  const selectedAssignee = pendingAssignee !== undefined ? pendingAssignee : request.assignee;

  // The picker must be able to show whoever is already assigned, even when the
  // assignable-users query is still in flight or failed outright — otherwise an
  // assigned request renders as blank, which reads as unassigned.
  const assigneeOptions: AssignableUser[] =
    request.assignee !== null && !assignableUsers.some((u) => u.id === request.assignee)
      ? [
          { id: request.assignee, display: request.assignee_display ?? 'Current assignee' },
          ...assignableUsers,
        ]
      : assignableUsers;

  // The backend rejects a close without a resolution note, so mirror that here
  // rather than letting the manager submit into a 400.
  const needsNote = !!nextStatus && requiresResolutionNote(nextStatus);
  const canSubmitStatus = !!nextStatus && (!needsNote || !!statusMessage.trim());

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full px-4 py-8 overflow-auto">
      <button
        type="button"
        className="flex items-center gap-2 mb-4 text-sm text-gray-600"
        onClick={() => navigate('/app/provisioning_requests')}
      >
        <ArrowLeft16 />
        Back to queue
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h2>{request.client_name}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {REQUEST_TYPE_LABEL[request.request_type]} · requested by{' '}
            {request.requester_display || '—'} ({REQUESTER_TEAM_LABEL[request.requester_team]}) on{' '}
            {formatDateTime(request.created_at)}
          </p>
        </div>
        <StatusBadge status={request.status} />
      </div>

      <div className="md:grid-cols-3 grid grid-cols-1 gap-8 mt-6">
        <div className="md:col-span-2">
          <dl className="sm:grid-cols-2 gap-x-6 grid grid-cols-1">
            <Field label="Needed by">{formatDate(request.needed_by)}</Field>
            <Field label="Assigned to">{request.assignee_display || 'Unassigned'}</Field>
            <Field label="Client contact">{request.client_contact_name || '—'}</Field>
            <Field label="Contact email">{request.client_contact_email || '—'}</Field>
            <Field label="Contact phone">{request.client_contact_phone || '—'}</Field>
          </dl>

          {request.notes && (
            <div className="mt-4">
              <p className="text-xs text-gray-500">Notes from the requester</p>
              <p className="text-sm whitespace-pre-line">{request.notes}</p>
            </div>
          )}

          {sip && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700">SIP trunk</h4>
              <dl className="sm:grid-cols-2 gap-x-6 grid grid-cols-1 mt-2">
                <Field label="Reason we can't use theirs">
                  {SIP_TRUNK_REASON_LABEL[sip.reason]}
                  {sip.reason_other && ` — ${sip.reason_other}`}
                </Field>
                <Field label="Call volume">
                  {callVolumeSummary(sip.monthly_call_volume, sip.daily_call_volume)}
                </Field>
                <Field label="Peak concurrent calls">{sip.peak_concurrent_calls}</Field>
                <Field label="Softphone users">{sip.softphone_users}</Field>
                <Field label="Locations">{sip.locations}</Field>
                <Field label="New DIDs needed">{sip.did_numbers_needed}</Field>
                <Field label="Porting">
                  {yesNo(sip.porting_required)}
                  {sip.porting_details && ` — ${sip.porting_details}`}
                </Field>
                <Field label="Call recording">{yesNo(sip.call_recording_required)}</Field>
                <Field label="Expected go-live">{formatDate(sip.expected_go_live)}</Field>
              </dl>
            </div>
          )}

          {equipment && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700">Equipment</h4>
              <dl className="sm:grid-cols-2 gap-x-6 grid grid-cols-1 mt-2">
                <Field label="Locations">{equipment.locations_count}</Field>
                <Field label="Devices">
                  {equipment.total_devices} total — {equipment.tv_count} TV, {equipment.kiosk_count}{' '}
                  kiosk, {equipment.tablet_count} tablet, {equipment.pc_count ?? 0} PC
                </Field>
                <Field label="Purchase order">{equipment.purchase_order_number}</Field>
                <Field label="PO evidence">
                  {equipment.purchase_order_file_url ? (
                    <a
                      href={equipment.purchase_order_file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-2.0 inline-flex items-center gap-1 underline"
                    >
                      <Paperclip14 />
                      View document
                    </a>
                  ) : (
                    <label className="text-sm">
                      <span className="text-red-600">Missing — </span>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        disabled={isUploading}
                        className="text-xs"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) attachPurchaseOrder(file);
                        }}
                      />
                    </label>
                  )}
                </Field>
                <Field label="Installation">
                  {INSTALLATION_TYPE_LABEL[equipment.installation_type]}
                </Field>
                <Field label="Requested install date">
                  {formatDate(equipment.requested_installation_date)}
                </Field>
                <Field label="Address">{equipment.installation_address || '—'}</Field>
                <Field label="Site network ready">{yesNo(equipment.network_ready)}</Field>
              </dl>
            </div>
          )}

          {phoneHardware && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700">Phone hardware</h4>
              <table className="w-full mt-2 text-sm">
                <thead>
                  <tr className="text-xs text-left text-gray-500">
                    <th className="py-1 font-normal">Location</th>
                    <th className="py-1 font-normal text-right">Phones</th>
                  </tr>
                </thead>
                <tbody>
                  {phoneHardware.locations.map((location) => (
                    <tr key={location.id ?? location.name} className="border-t">
                      <td className="py-1.5">{location.name}</td>
                      <td className="py-1.5 text-right">{location.phone_count}</td>
                    </tr>
                  ))}
                  <tr className="font-medium border-t">
                    <td className="py-1.5">Total</td>
                    <td className="py-1.5 text-right">{phoneHardware.total_phones}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {request.closed_at && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700">Resolution</h4>
              <p className="mt-1 text-sm text-gray-500">
                {STATUS_LABEL[request.status]} by {request.closed_by_display || '—'} on{' '}
                {formatDateTime(request.closed_at)}
              </p>
              <p className="mt-1 text-sm whitespace-pre-line">{request.resolution_notes}</p>
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700">Assigned to</h4>
          <select
            className="input w-full mt-2"
            aria-label="Assignee"
            disabled={isAssigning}
            value={selectedAssignee ?? ''}
            onChange={(event) => {
              const next = event.target.value ? Number(event.target.value) : null;
              setPendingAssignee(next);
              assign(next);
            }}
          >
            <option value="">Unassigned</option>
            {assigneeOptions.map((user) => (
              <option key={user.id} value={user.id}>
                {user.display}
              </option>
            ))}
          </select>

          <h4 className="mt-8 text-sm font-medium text-gray-700">Move this along</h4>
          <select
            className="input w-full mt-2"
            aria-label="New status"
            value={nextStatus}
            onChange={(event) => setNextStatus(event.target.value as RequestStatus | '')}
          >
            <option value="">Change status…</option>
            {STATUS_OPTIONS.filter((option) => option.value !== request.status).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <textarea
            className="input w-full mt-2"
            rows={3}
            maxLength={COMMENT_MAX_LENGTH}
            placeholder={needsNote ? 'Required: what was done?' : 'Optional note about this change'}
            value={statusMessage}
            onChange={(event) => setStatusMessage(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-secondary w-full mt-2"
            disabled={!canSubmitStatus || isChangingStatus}
            onClick={() => changeStatus()}
          >
            {isChangingStatus ? 'Saving…' : 'Update status'}
          </button>

          <h4 className="mt-8 text-sm font-medium text-gray-700">Activity</h4>
          <ul className="mt-2">
            {request.events.map((event) => (
              <li key={event.id} className="py-2 text-sm border-b">
                <div className="text-xs text-gray-500">
                  {event.author_display || '—'} · {formatDateTime(event.created_at)}
                </div>
                {describeEvent(event) && <div className="font-medium">{describeEvent(event)}</div>}
                {event.message && <div className="whitespace-pre-line">{event.message}</div>}
              </li>
            ))}
          </ul>

          <textarea
            className="input w-full mt-3"
            rows={2}
            // Mirrors the backend's CharField bound so a long note is trimmed
            // here instead of coming back as a 400.
            maxLength={COMMENT_MAX_LENGTH}
            placeholder="Add a note…"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-ghost w-full mt-2"
            disabled={!comment.trim() || isCommenting}
            onClick={() => addComment()}
          >
            Add note
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestDetail;
